import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { TopUpInfo } from "@/api/admin/diners";
import { useCurrencyQuery } from "@/api/admin/useCurrencyQuery";
import {
  CustomButton,
  CustomLabel,
  FieldErrorMessage,
} from "@/components/atoms";
import { DialogClose } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { FormattedInputField } from "@/components/units/FundingForm/FormattingInput";
import { amountPattern, parseAmountValue } from "@/utils/parseAmountValue";

import { type ApproveTopUpFormValues, ApproveTopUpSchema } from "./types";

const ApproveActionForm = ({
  topUp,
  onConfirm,
  isPending,
}: {
  topUp: TopUpInfo;
  onConfirm: (amount: number) => void;
  isPending: boolean;
}) => {
  const currency = useCurrencyQuery(topUp.currency);
  const decimals = currency?.decimals ?? 2;

  const form = useForm<ApproveTopUpFormValues>({
    resolver: zodResolver(
      ApproveTopUpSchema(topUp.requestedAmount, topUp.currency)
    ),
    defaultValues: {
      approvedAmount: topUp.requestedAmount,
    },
    mode: "onChange",
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(data => onConfirm(data.approvedAmount))}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="approvedAmount"
          render={({ field }) => (
            <FormItem className="relative">
              <CustomLabel
                label={`Approved Amount${topUp.currency ? ` (${topUp.currency})` : ""}`}
                htmlFor="approvedAmount"
              />
              <FormControl>
                <FormattedInputField
                  nameField="approvedAmount"
                  field={field}
                  placeholder={(0).toFixed(decimals)}
                  allowDecimals={true}
                  validationPattern={amountPattern(decimals)}
                  parseValue={value => parseAmountValue(value, decimals)}
                />
              </FormControl>
              <FieldErrorMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-between gap-3 mt-4">
          <DialogClose asChild>
            <CustomButton variant="light" className="flex-1">
              Cancel
            </CustomButton>
          </DialogClose>
          <CustomButton
            className="flex-1"
            isPending={isPending}
            disabled={!form.formState.isValid}
          >
            Confirm Approval
          </CustomButton>
        </div>
      </form>
    </Form>
  );
};

export default ApproveActionForm;
